import * as React from "react";
import type { RecentItem } from "../types/recent";

export type UseRecentsOptions = {
  storageKey?: string;
  maxItems?: number;
  ttlMs?: number;
};

export type UseRecentsReturn = {
  items: RecentItem[];
  record: (item: Omit<RecentItem, "lastAccessedAt">) => void;
  remove: (id: string) => void;
  clear: () => void;
  touch: (id: string) => void;
  withRecentOnClick: <T extends HTMLElement = HTMLElement>(
    item: Omit<RecentItem, "lastAccessedAt">,
    onClick?: React.MouseEventHandler<T>,
  ) => React.MouseEventHandler<T>;
};

const DEFAULT_STORAGE_KEY = "RECENTS::v1";
const DEFAULT_MAX_ITEMS = 50;

// In-memory fallback store (session-only)
const memoryStore = new Map<string, string>();

function isLocalStorageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const k = "__RECENTS_PROBE__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function readFromLocalOrMemory(storageKey: string): string | null {
  if (typeof window === "undefined") {
    return memoryStore.get(storageKey) ?? null;
  }
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return memoryStore.get(storageKey) ?? null;
  }
}

function writeToMemory(storageKey: string, value: string): void {
  memoryStore.set(storageKey, value);
}

function robustPersist(
  storageKey: string,
  items: RecentItem[],
  preferLocalStorage: boolean,
): void {
  const payload = JSON.stringify({ items });
  const tryLocal = (): void => {
    if (typeof window === "undefined") throw new Error("no-window");
    window.localStorage.setItem(storageKey, payload);
  };

  try {
    if (preferLocalStorage) {
      tryLocal();
    } else {
      writeToMemory(storageKey, payload);
    }
  } catch {
    if (preferLocalStorage) {
      try {
        const kept = Math.max(0, Math.ceil(items.length / 2));
        const trimmed = items.slice(0, kept);
        const payload2 = JSON.stringify({ items: trimmed });
        window.localStorage.setItem(storageKey, payload2);
      } catch {
        try {
          const kept = Math.max(0, Math.ceil(items.length / 2));
          const trimmed = items.slice(0, kept);
          writeToMemory(storageKey, JSON.stringify({ items: trimmed }));
        } catch {}
      }
    }
  }
}

function parseItems(raw: string | null): RecentItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { items?: unknown };
    if (!parsed || !Array.isArray((parsed as any).items)) return [];
    return (parsed as any).items.filter(
      (x: any) =>
        x && typeof x.id === "string" && typeof x.lastAccessedAt === "number",
    );
  } catch {
    return [];
  }
}

function filterExpired(
  items: RecentItem[],
  now: number,
  ttlMs?: number,
): RecentItem[] {
  if (!ttlMs || ttlMs <= 0) return items;
  const cutoff = now - ttlMs;
  return items.filter((i) => i.lastAccessedAt >= cutoff);
}

function stableSortByLastAccessedDesc(items: RecentItem[]): RecentItem[] {
  return items
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      if (a.item.lastAccessedAt !== b.item.lastAccessedAt) {
        return b.item.lastAccessedAt - a.item.lastAccessedAt;
      }
      return a.idx - b.idx;
    })
    .map((x) => x.item);
}

/**
 * React hook for managing "Recently Viewed" items via localStorage with robust fallbacks.
 */
export function useRecents(options?: UseRecentsOptions): UseRecentsReturn {
  const storageKey = options?.storageKey ?? DEFAULT_STORAGE_KEY;
  const maxItems = Math.max(1, options?.maxItems ?? DEFAULT_MAX_ITEMS);
  const ttlMs = options?.ttlMs;

  const preferLocal = React.useMemo(isLocalStorageAvailable, []);
  const [items, setItems] = React.useState<RecentItem[]>(() => {
    const now = Date.now();
    const raw = readFromLocalOrMemory(storageKey);
    const parsed = parseItems(raw);
    const filtered = filterExpired(parsed, now, ttlMs);
    const sorted = stableSortByLastAccessedDesc(filtered);
    robustPersist(storageKey, sorted.slice(0, maxItems), preferLocal);
    return sorted.slice(0, maxItems);
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key !== storageKey) return;
      const now = Date.now();
      const payload =
        typeof e.newValue === "string"
          ? e.newValue
          : readFromLocalOrMemory(storageKey);
      const parsed = parseItems(payload);
      const filtered = filterExpired(parsed, now, ttlMs);
      const sorted = stableSortByLastAccessedDesc(filtered).slice(0, maxItems);
      setItems(sorted);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, maxItems, ttlMs]);

  const persistAndSet = React.useCallback(
    (next: RecentItem[]) => {
      const now = Date.now();
      const filtered = filterExpired(next, now, ttlMs);
      const sorted = stableSortByLastAccessedDesc(filtered).slice(0, maxItems);
      setItems(sorted);
      robustPersist(storageKey, sorted, preferLocal);
    },
    [maxItems, preferLocal, storageKey, ttlMs],
  );

  const record = React.useCallback<UseRecentsReturn["record"]>(
    (item) => {
      const now = Date.now();
      const current = items;
      const without = current.filter((i) => i.id !== item.id);
      const next: RecentItem[] = [
        { ...item, lastAccessedAt: now },
        ...without,
      ].slice(0, maxItems);
      persistAndSet(next);
    },
    [items, maxItems, persistAndSet],
  );

  const touch = React.useCallback<UseRecentsReturn["touch"]>(
    (id) => {
      const now = Date.now();
      const idx = items.findIndex((i) => i.id === id);
      if (idx === -1) return;
      const target = items[idx]!;
      const updated: RecentItem = { ...target, lastAccessedAt: now };
      const without = items.filter((i) => i.id !== id);
      persistAndSet([updated, ...without]);
    },
    [items, persistAndSet],
  );

  const remove = React.useCallback<UseRecentsReturn["remove"]>(
    (id) => {
      const next = items.filter((i) => i.id !== id);
      persistAndSet(next);
    },
    [items, persistAndSet],
  );

  const clear = React.useCallback<UseRecentsReturn["clear"]>(() => {
    setItems([]);
    robustPersist(storageKey, [], preferLocal);
  }, [preferLocal, storageKey]);

  const withRecentOnClick = React.useCallback<
    UseRecentsReturn["withRecentOnClick"]
  >(
    (item, onClick) => {
      return (e) => {
        record(item);
        if (onClick) onClick(e);
      };
    },
    [record],
  );

  return { items, record, remove, clear, touch, withRecentOnClick };
}

export default useRecents;
